//src/pages/GruposEmpresariais.jsx

import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import {
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Edit3,
  Layers3,
  Plus,
  RefreshCcw,
  Save,
  Search,
  Trash2,
  X
} from "lucide-react";
import "./GruposEmpresariais.css";

const FORM_INICIAL = {
  id: null,
  nome: "",
  ativo: true,
  empresas: []
};

export default function GruposEmpresariais() {
  const [grupos, setGrupos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [formulario, setFormulario] = useState(FORM_INICIAL);
  const [busca, setBusca] = useState("");
  const [buscaEmpresa, setBuscaEmpresa] = useState("");
  const [grupoAberto, setGrupoAberto] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const carregarDados = async () => {
    setLoading(true);
    setErro("");

    try {
      const [gruposResp, empresasResp] = await Promise.all([
        axios.get("/admin/grupos-empresariais"),
        axios.get("/admin/grupos-empresariais/empresas-disponiveis")
      ]);

      setGrupos(
        Array.isArray(gruposResp.data?.grupos)
          ? gruposResp.data.grupos
          : []
      );

      setEmpresas(
        Array.isArray(empresasResp.data?.empresas)
          ? empresasResp.data.empresas
          : []
      );
    } catch (err) {
      setErro(
        err.response?.data?.error ||
          "Não foi possível carregar os grupos empresariais."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const gruposFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    if (!termo) return grupos;

    return grupos.filter((grupo) => {
      const conteudo = [
        grupo.nome,
        ...(grupo.empresas || []).flatMap((empresa) => [
          empresa.razao_social,
          empresa.cnpj
        ])
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return conteudo.includes(termo);
    });
  }, [grupos, busca]);

  const empresasFiltradas = useMemo(() => {
    const termo = buscaEmpresa.trim().toLowerCase();

    return empresas.filter((empresa) => {
      const pertenceOutroGrupo =
        empresa.grupo_id &&
        Number(empresa.grupo_id) !== Number(formulario.id);

      if (pertenceOutroGrupo) return false;

      if (!termo) return true;

      return `${empresa.razao_social || ""} ${empresa.cnpj || ""}`
        .toLowerCase()
        .includes(termo);
    });
  }, [empresas, buscaEmpresa, formulario.id]);

  const abrirNovoGrupo = () => {
    setFormulario(FORM_INICIAL);
    setBuscaEmpresa("");
    setErro("");
    setSucesso("");
    setModalAberto(true);
  };

  const abrirEdicao = (grupo) => {
    setFormulario({
      id: Number(grupo.id),
      nome: grupo.nome || "",
      ativo: Boolean(grupo.ativo),
      empresas: (grupo.empresas || []).map((empresa) => empresa.cnpj)
    });

    setBuscaEmpresa("");
    setErro("");
    setSucesso("");
    setModalAberto(true);
  };

  const fecharModal = () => {
    if (salvando) return;

    setModalAberto(false);
    setFormulario(FORM_INICIAL);
    setBuscaEmpresa("");
  };

  const alternarEmpresa = (cnpj) => {
    setFormulario((atual) => {
      const selecionada = atual.empresas.includes(cnpj);

      return {
        ...atual,
        empresas: selecionada
          ? atual.empresas.filter((item) => item !== cnpj)
          : [...atual.empresas, cnpj]
      };
    });
  };

  const salvarGrupo = async (event) => {
    event.preventDefault();

    const nome = formulario.nome.trim();

    if (!nome) {
      setErro("Informe o nome do grupo empresarial.");
      return;
    }

    if (!formulario.empresas.length) {
      setErro("Selecione pelo menos uma empresa para o grupo.");
      return;
    }

    setSalvando(true);
    setErro("");
    setSucesso("");

    try {
      const payload = {
        nome,
        ativo: Boolean(formulario.ativo),
        empresas: formulario.empresas
      };

      if (formulario.id) {
        await axios.put(
          `/admin/grupos-empresariais/${formulario.id}`,
          payload
        );

        setSucesso("Grupo empresarial atualizado com sucesso.");
      } else {
        await axios.post("/admin/grupos-empresariais", payload);
        setSucesso("Grupo empresarial criado com sucesso.");
      }

      await carregarDados();
      setModalAberto(false);
      setFormulario(FORM_INICIAL);
    } catch (err) {
      setErro(
        err.response?.data?.error ||
          "Não foi possível salvar o grupo empresarial."
      );
    } finally {
      setSalvando(false);
    }
  };

  const excluirGrupo = async (grupo) => {
    const confirmou = window.confirm(
      `Excluir o grupo "${grupo.nome}"? Os CNPJs vinculados ficarão disponíveis para outro grupo.`
    );

    if (!confirmou) return;

    setErro("");
    setSucesso("");

    try {
      await axios.delete(`/admin/grupos-empresariais/${grupo.id}`);
      setSucesso("Grupo empresarial excluído com sucesso.");
      await carregarDados();
    } catch (err) {
      setErro(
        err.response?.data?.error ||
          "Não foi possível excluir o grupo empresarial."
      );
    }
  };

  const formatarCnpj = (cnpj) => {
    const numeros = String(cnpj || "").replace(/\D/g, "");

    if (numeros.length !== 14) return cnpj || "-";

    return numeros.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5"
    );
  };

  const totalEmpresasAgrupadas = grupos.reduce(
    (total, grupo) => total + (grupo.empresas?.length || 0),
    0
  );

  return (
    <div className="gruposEmpresariais-page">
      <header className="gruposEmpresariais-header">
        <div>
          <span className="gruposEmpresariais-kicker">
            Administração de empresas
          </span>
          <h1>Grupos empresariais</h1>
          <p>
            Vincule empresas com CNPJs diferentes para permitir operações
            entre integrantes do mesmo grupo.
          </p>
        </div>

        <button
          type="button"
          className="gruposEmpresariais-primaryButton"
          onClick={abrirNovoGrupo}
        >
          <Plus size={17} />
          Novo grupo
        </button>
      </header>

      {erro && (
        <div className="gruposEmpresariais-alert erro">{erro}</div>
      )}

      {sucesso && (
        <div className="gruposEmpresariais-alert sucesso">{sucesso}</div>
      )}

      <section className="gruposEmpresariais-summary">
        <article className="gruposEmpresariais-summaryCard">
          <Layers3 size={21} />
          <div>
            <span>Grupos cadastrados</span>
            <strong>{grupos.length}</strong>
          </div>
        </article>

        <article className="gruposEmpresariais-summaryCard">
          <Building2 size={21} />
          <div>
            <span>Empresas agrupadas</span>
            <strong>{totalEmpresasAgrupadas}</strong>
          </div>
        </article>

        <article className="gruposEmpresariais-summaryCard">
          <CheckCircle2 size={21} />
          <div>
            <span>Grupos ativos</span>
            <strong>
              {grupos.filter((grupo) => Boolean(grupo.ativo)).length}
            </strong>
          </div>
        </article>
      </section>

      <section className="gruposEmpresariais-panel">
        <div className="gruposEmpresariais-toolbar">
          <div className="gruposEmpresariais-search">
            <Search size={16} />
            <input
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
              placeholder="Buscar grupo, empresa ou CNPJ"
            />
          </div>

          <button
            type="button"
            className="gruposEmpresariais-secondaryButton"
            onClick={carregarDados}
            disabled={loading}
          >
            <RefreshCcw
              size={16}
              className={loading ? "gruposEmpresariais-spin" : ""}
            />
            Atualizar
          </button>
        </div>

        <div className="gruposEmpresariais-list">
          {gruposFiltrados.map((grupo) => {
            const aberto = Number(grupoAberto) === Number(grupo.id);
            const quantidade = grupo.empresas?.length || 0;

            return (
              <article
                className="gruposEmpresariais-groupCard"
                key={grupo.id}
              >
                <div className="gruposEmpresariais-groupMain">
                  <button
                    type="button"
                    className="gruposEmpresariais-expandButton"
                    onClick={() =>
                      setGrupoAberto((atual) =>
                        Number(atual) === Number(grupo.id)
                          ? null
                          : grupo.id
                      )
                    }
                  >
                    {aberto ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </button>

                  <div className="gruposEmpresariais-groupIdentity">
                    <div className="gruposEmpresariais-groupIcon">
                      <Layers3 size={19} />
                    </div>

                    <div>
                      <h2>{grupo.nome}</h2>
                      <p>
                        {quantidade} empresa
                        {quantidade === 1 ? "" : "s"} vinculada
                        {quantidade === 1 ? "" : "s"}
                      </p>
                    </div>
                  </div>

                  <span
                    className={`gruposEmpresariais-status ${
                      grupo.ativo ? "ativo" : "inativo"
                    }`}
                  >
                    {grupo.ativo ? "Ativo" : "Inativo"}
                  </span>

                  <div className="gruposEmpresariais-groupActions">
                    <button
                      type="button"
                      onClick={() => abrirEdicao(grupo)}
                    >
                      <Edit3 size={16} />
                      Editar
                    </button>

                    <button
                      type="button"
                      className="danger"
                      onClick={() => excluirGrupo(grupo)}
                    >
                      <Trash2 size={16} />
                      Excluir
                    </button>
                  </div>
                </div>

                {aberto && (
                  <div className="gruposEmpresariais-companies">
                    {(grupo.empresas || []).map((empresa) => (
                      <div
                        className="gruposEmpresariais-companyRow"
                        key={empresa.cnpj}
                      >
                        <div>
                          <strong>
                            {empresa.razao_social ||
                              "Empresa sem razão social"}
                          </strong>
                          <span>{formatarCnpj(empresa.cnpj)}</span>
                        </div>

                        <span
                          className={`gruposEmpresariais-companyStatus ${
                            empresa.ativa ? "ativo" : "inativo"
                          }`}
                        >
                          {empresa.ativa
                            ? "Empresa ativa"
                            : "Empresa inativa"}
                        </span>
                      </div>
                    ))}

                    {!quantidade && (
                      <div className="gruposEmpresariais-empty">
                        Nenhuma empresa vinculada.
                      </div>
                    )}
                  </div>
                )}
              </article>
            );
          })}

          {!loading && !gruposFiltrados.length && (
            <div className="gruposEmpresariais-empty">
              Nenhum grupo empresarial encontrado.
            </div>
          )}

          {loading && (
            <div className="gruposEmpresariais-empty">
              Carregando grupos empresariais...
            </div>
          )}
        </div>
      </section>

      {modalAberto && (
        <div
          className="gruposEmpresariais-modalOverlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              fecharModal();
            }
          }}
        >
          <form
            className="gruposEmpresariais-modal"
            onSubmit={salvarGrupo}
          >
            <div className="gruposEmpresariais-modalHeader">
              <div>
                <span className="gruposEmpresariais-kicker">
                  {formulario.id ? "Edição" : "Novo cadastro"}
                </span>
                <h2>
                  {formulario.id
                    ? "Editar grupo empresarial"
                    : "Criar grupo empresarial"}
                </h2>
              </div>

              <button
                type="button"
                className="gruposEmpresariais-closeButton"
                onClick={fecharModal}
              >
                <X size={20} />
              </button>
            </div>

            <div className="gruposEmpresariais-modalBody">
              <div className="gruposEmpresariais-formGrid">
                <label className="gruposEmpresariais-field">
                  <span>Nome do grupo</span>
                  <input
                    value={formulario.nome}
                    onChange={(event) =>
                      setFormulario((atual) => ({
                        ...atual,
                        nome: event.target.value
                      }))
                    }
                    placeholder="Ex.: Grupo Cardinali"
                    maxLength={150}
                    autoFocus
                  />
                </label>

                <label className="gruposEmpresariais-toggleField">
                  <input
                    type="checkbox"
                    checked={formulario.ativo}
                    onChange={(event) =>
                      setFormulario((atual) => ({
                        ...atual,
                        ativo: event.target.checked
                      }))
                    }
                  />

                  <span>
                    <strong>Grupo ativo</strong>
                    <small>
                      Somente grupos ativos liberam operações entre
                      CNPJs diferentes.
                    </small>
                  </span>
                </label>
              </div>

              <div className="gruposEmpresariais-companySelectorHeader">
                <div>
                  <h3>Empresas do grupo</h3>
                  <p>
                    Cada empresa pode pertencer a somente um grupo
                    empresarial.
                  </p>
                </div>

                <span>
                  {formulario.empresas.length} selecionada
                  {formulario.empresas.length === 1 ? "" : "s"}
                </span>
              </div>

              <div className="gruposEmpresariais-search modalSearch">
                <Search size={16} />
                <input
                  value={buscaEmpresa}
                  onChange={(event) =>
                    setBuscaEmpresa(event.target.value)
                  }
                  placeholder="Buscar empresa ou CNPJ"
                />
              </div>

              <div className="gruposEmpresariais-companySelector">
                {empresasFiltradas.map((empresa) => {
                  const selecionada =
                    formulario.empresas.includes(empresa.cnpj);

                  return (
                    <label
                      className={`gruposEmpresariais-companyOption ${
                        selecionada ? "selecionada" : ""
                      }`}
                      key={empresa.cnpj}
                    >
                      <input
                        type="checkbox"
                        checked={selecionada}
                        onChange={() =>
                          alternarEmpresa(empresa.cnpj)
                        }
                      />

                      <div>
                        <strong>{empresa.razao_social}</strong>
                        <span>{formatarCnpj(empresa.cnpj)}</span>
                      </div>
                    </label>
                  );
                })}

                {!empresasFiltradas.length && (
                  <div className="gruposEmpresariais-empty">
                    Nenhuma empresa disponível para este filtro.
                  </div>
                )}
              </div>
            </div>

            <div className="gruposEmpresariais-modalFooter">
              <button
                type="button"
                className="gruposEmpresariais-secondaryButton"
                onClick={fecharModal}
                disabled={salvando}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className="gruposEmpresariais-primaryButton"
                disabled={salvando}
              >
                {salvando ? (
                  <RefreshCcw
                    size={16}
                    className="gruposEmpresariais-spin"
                  />
                ) : (
                  <Save size={16} />
                )}

                {formulario.id
                  ? "Salvar alterações"
                  : "Criar grupo"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
